import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { rfqProcess, qualitySteps, heroImages } from '../data/mockData';
import { CheckCircle2, ArrowRight, Shield } from 'lucide-react';

export const QualityProcess = () => {
  const documentation = [
    'Inspection reports with dimensional data',
    'Material certifications (MTR)',
    'First article inspection reports (FAIR)',
    'Certificates of Conformance (CoC)',
    'Process capability studies (Cpk)',
    'Traceability documentation'
  ];

  const supplierQualification = [
    'On-site facility audits and assessments',
    'Sample validation and capability testing',
    'Quality system review and certification verification',
    'Ongoing performance monitoring and scorecards'
  ];

  const faqs = [
    {
      question: 'What are typical lead times for different processes?',
      answer: 'Lead times vary by process and volume: CNC machining (2-4 weeks for prototypes, 4-8 weeks for production), Injection molding (6-12 weeks including tooling for first run, 3-5 weeks for repeat orders), Die casting (12-16 weeks including tooling, 4-6 weeks for repeat), Sheet metal (1-3 weeks). We provide specific lead times during the quote process.'
    },
    {
      question: 'What are your minimum order quantities (MOQ)?',
      answer: 'Our MOQ varies by process: We accept prototype quantities as low as 1-10 pieces for CNC machining and sheet metal. Injection molding typically has MOQs of 100-500 pieces depending on part size. Die casting MOQs are typically 500-1,000 pieces. We work with you to find economical solutions for your volume requirements.'
    },
    {
      question: 'How do you handle design changes or revisions?',
      answer: 'We use a formal Engineering Change Notice (ECN) process. All changes are documented, reviewed for impact on cost and lead time, and require approval before implementation. We provide updated quotes when changes affect pricing. Our goal is to accommodate necessary changes while maintaining project timelines.'
    },
    {
      question: 'Do you require an NDA before reviewing drawings?',
      answer: 'We can provide a mutual Non-Disclosure Agreement (NDA) upon request before you share proprietary information. We treat all customer information as confidential and maintain strict data security protocols. Simply indicate in your RFQ if you require an NDA, and we will provide one promptly.'
    },
    {
      question: 'What shipping and delivery terms do you offer?',
      answer: 'We offer flexible shipping options including DDP (Delivered Duty Paid) for hassle-free international delivery, and standard FOB terms. We work with major carriers and freight forwarders to optimize cost and transit time. Expedited shipping options are available when needed. All shipments include tracking and insurance.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-accent/10 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-accent" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-foreground mb-6">Quality & Process</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive quality control and streamlined RFQ process ensuring consistent, reliable parts that meet your specifications
          </p>
        </div>
      </section>

      {/* RFQ Workflow */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Our RFQ Workflow</h2>
            <p className="text-lg text-muted-foreground">From inquiry to delivery in four transparent steps</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rfqProcess.map((step, index) => (
              <Card key={index} className="relative hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="text-7xl font-bold text-accent/10 absolute top-4 right-4">{step.step}</div>
                  <div className="text-2xl font-bold text-accent mb-4">Step {step.step}</div>
                  <h3 className="text-lg font-bold mb-3">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </CardContent>
                {index < rfqProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <div className="h-6 w-6 bg-accent rounded-full flex items-center justify-center">
                      <ArrowRight className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Control Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Quality Control Process</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Multi-stage inspection ensures every part meets specifications before shipment
              </p>

              <div className="space-y-4">
                {qualitySteps.map((step, index) => (
                  <Card key={index} className="hover:shadow-md transition-smooth">
                    <CardContent className="p-5">
                      <div className="flex items-start space-x-3">
                        <div className="h-8 w-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <CheckCircle2 className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <h3 className="font-bold mb-1">{step.title}</h3>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <img 
                src={heroImages.qualityControl} 
                alt="Quality Control" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">Documentation We Provide</h3>
                <p className="text-muted-foreground mb-6">
                  Comprehensive documentation for complete traceability and compliance
                </p>
                <ul className="space-y-3">
                  {documentation.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground mt-6 italic">
                  *Documentation levels can be customized based on project requirements
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">Supplier Qualification</h3>
                <p className="text-muted-foreground mb-6">
                  Rigorous qualification process for our Malaysia and China manufacturing partners
                </p>
                <ul className="space-y-3">
                  {supplierQualification.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground mt-6">
                  All suppliers maintain ISO certifications and undergo regular performance reviews
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Submit your RFQ today and experience our streamlined process
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
